/*
 * Documents: the folder tree, what is in each folder, and the register of
 * everything the person may see (Tenant/Documents controllers).
 */
import { departments } from "@/mock/masters";
import { users } from "@/mock/admin";
import { ok, created, notFound, invalid, matches, nextId, daysAgo, paginate } from "./util";

const ME = 1; // Jane Wanjiku, the sample signed-in person

// Folders as the Folder model serialises them.
const F = (id, name, folder_type, parent_folder_id = null, extra = {}) => ({
  id, name, folder_type, parent_folder_id, user_id: null, created_by: ME,
  created_at: daysAgo(400 - id), updated_at: daysAgo(30), ...extra,
});

const folders = [
  F(1, "Contracts & Agreements", "organisation"),
  F(2, "Proposals & Quotations", "organisation"),
  F(3, "Policies & Templates", "organisation"),
  F(4, "Implementation Evidence", "organisation"),
  F(5, "Harambee Sacco Society", "organisation", 1),
  F(6, "Savannah Hotels Group", "organisation", 1),
  F(7, "Kilimo Fresh Exporters", "organisation", 2),
  F(8, "Lakeview SACCO", "organisation", 2),
  F(9, "UAT sign-offs", "organisation", 4),
  // The API has two folder types: the company's (organisation) and each user's own (user).
  ...departments.map((d, i) => F(20 + i, d.name, "organisation")),
  ...users.filter((u) => u.status === "active").map((u) => F(40 + u.id, u.name, "user", null, { user_id: u.id })),
];

// Documents with their current version, as the Document model with currentVersion loads.
const KINDS = {
  pdf: "application/pdf",
  word: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  excel: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  image: "image/png",
};
const D = (id, folder_id, name, description, kind, size, version, ago) => ({
  id, folder_id, name, description, uploaded_by: ME, created_at: daysAgo(ago), updated_at: daysAgo(Math.max(0, ago - 3)),
  current_version: {
    id: id * 10 + version, document_id: id, version_number: version, file_type_category: kind,
    mime_type: KINDS[kind], size_bytes: size, storage_url: `sample/${id}`, created_at: daysAgo(ago),
  },
});

const documents = [
  D(1, 5, "Master Services Agreement – Harambee Sacco", "Signed MSA, 3-year term", "pdf", 842_113, 2, 250),
  D(2, 5, "Data Processing Addendum", "Signed with the MSA", "pdf", 311_402, 1, 248),
  D(3, 6, "HR & Payroll subscription agreement", "Savannah Hotels Group, renewal due Q1", "pdf", 512_998, 1, 330),
  D(4, 6, "Purchase order PO-77821", null, "pdf", 98_204, 1, 329),
  D(5, 7, "Proposal – Kilimo Fresh Exporters", "CRM Business, 40 users", "word", 1_204_551, 3, 21),
  D(6, 7, "Quotation QUO-2026-0142", "Sent 14 days ago", "pdf", 145_330, 1, 14),
  D(7, 8, "Quotation QUO-2026-0151", "Awaiting finance approval", "pdf", 151_007, 1, 6),
  D(8, 8, "Pricing workings", "Discount within approval limit", "excel", 64_220, 2, 6),
  D(9, 3, "Standard quotation template", "Use for all new quotations", "word", 220_413, 4, 180),
  D(10, 3, "Discount approval policy", "Approved by Management", "pdf", 401_118, 1, 200),
  D(11, 3, "Customer onboarding checklist", null, "excel", 51_009, 2, 150),
  D(12, 9, "UAT report – Harambee Sacco", "Signed off by the client PM", "pdf", 702_441, 1, 210),
  D(13, 9, "Go-live sign-off – Pwani Logistics", null, "pdf", 233_870, 1, 60),
  D(14, 4, "Data migration reconciliation", "Legacy CRM vs DeeCRM360 counts", "excel", 388_120, 1, 58),
  D(15, 20, "Q3 sales targets", "By team and person", "excel", 74_551, 2, 40),
  D(16, 20, "Sales playbook 2026", null, "pdf", 2_310_004, 1, 120),
  D(17, 21, "Health score methodology", "How customer health is scored", "word", 180_330, 1, 90),
  D(18, 23, "Support SLA matrix", "Response and resolution targets", "pdf", 120_900, 2, 75),
  D(19, 24, "Campaign brief – HR Summit", null, "word", 96_440, 1, 33),
  D(20, 25, "Revenue recognition notes", "Finance only", "pdf", 205_772, 1, 45),
  D(21, 41, "My targets Q3", null, "excel", 22_300, 1, 40),
  D(22, 41, "Site visit photo – Lakeview SACCO", null, "image", 1_840_221, 1, 9),
];

const folderById = (id) => folders.find((f) => String(f.id) === String(id));

/** Folder names from the top down, as the register shows them. */
const pathOf = (folder) => {
  const names = [];
  for (let f = folder; f; f = f.parent_folder_id ? folderById(f.parent_folder_id) : null) names.unshift(f.name);
  return names.join(" / ") || "—";
};

// Everything under a set of folders, for the overview's counts.
const underRoots = (roots) => {
  const ids = new Set(roots.map((f) => f.id));
  let grew = true;
  while (grew) {
    grew = false;
    folders.forEach((f) => {
      if (f.parent_folder_id && ids.has(f.parent_folder_id) && !ids.has(f.id)) {
        ids.add(f.id);
        grew = true;
      }
    });
  }
  return ids;
};

const present = (d) => {
  const folder = folderById(d.folder_id);
  const v = d.current_version;
  return {
    id: d.id,
    name: d.name,
    description: d.description,
    folder: { id: d.folder_id, name: folder?.name ?? null, type: folder?.folder_type ?? null, path: pathOf(folder) },
    file_type: v?.file_type_category ?? null,
    mime_type: v?.mime_type ?? null,
    size_bytes: v ? Number(v.size_bytes) : null,
    version: v?.version_number ?? null,
    available: Boolean(v?.storage_url),
    uploaded_on: d.created_at.slice(0, 10),
    updated_on: d.updated_at.slice(0, 10),
  };
};

const unique = (list) => [...new Set(list.filter(Boolean))].sort();

// A link the browser can open: a blob URL in the page (Chrome blocks opening data: URLs), a data: URL elsewhere.
const fileUrl = (text, type) =>
  typeof Blob !== "undefined" && typeof URL?.createObjectURL === "function" && typeof window !== "undefined"
    ? URL.createObjectURL(new Blob([text], { type }))
    : `data:${type};charset=utf-8,${encodeURIComponent(text)}`;

const categorize = (fileName = "") => {
  const ext = String(fileName).split(".").pop().toLowerCase();
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return "image";
  if (ext === "pdf") return "pdf";
  if (["doc", "docx"].includes(ext)) return "word";
  if (["xls", "xlsx"].includes(ext)) return "excel";
  return "other";
};

export default [
  ["get", "tenant/folders-overview", () => {
    const types = [
      ["organisation", "Organisation"],
      ["user", "Personal folders"],
    ];
    return ok(types.map(([type, label]) => {
      const roots = folders.filter((f) => f.folder_type === type && !f.parent_folder_id);
      const ids = underRoots(roots);
      return { type, label, folder_count: roots.length, document_count: documents.filter((d) => ids.has(d.folder_id)).length };
    }));
  }],

  ["get", "tenant/folders", ({ query }) => {
    const type = query.type || "organisation";
    const parent = query.parent_folder_id;
    return ok(folders.filter((f) => f.folder_type === type && (parent ? String(f.parent_folder_id) === String(parent) : !f.parent_folder_id)));
  }],

  ["get", "tenant/folders/:id", ({ params }) => {
    const folder = folderById(params.id);
    if (!folder) return notFound("Folder not found");
    const owner = users.find((u) => u.id === folder.user_id);
    return ok({
      folder: {
        ...folder,
        children: folders.filter((f) => f.parent_folder_id === folder.id),
        user: owner ? { id: owner.id, name: owner.name, email: owner.email } : null,
      },
      documents: documents.filter((d) => d.folder_id === folder.id),
      is_admin: true,
    });
  }],

  ["post", "tenant/folders", ({ body }) => {
    const parent = folderById(body.parent_folder_id);
    if (!parent) return [403, { status: "error", code: 403, message: "You are not an administrator of this folder", data: null, errors: null }];
    if (!String(body.name ?? "").trim()) return invalid({ name: ["The name field is required."] });
    const folder = F(nextId(folders), body.name.trim(), parent.folder_type, parent.id, { user_id: parent.user_id, created_at: new Date().toISOString(), updated_at: new Date().toISOString() });
    folders.push(folder);
    return created(folder, "Folder created");
  }],

  ["get", "tenant/documents-library", ({ query }) => {
    const visible = documents
      .filter((d) => {
        const folder = folderById(d.folder_id);
        return matches(query.search, d.name, d.description, folder?.name)
          && (!query.folder_type || folder?.folder_type === query.folder_type)
          && (!query.folder_id || String(d.folder_id) === String(query.folder_id))
          && (!query.from || d.created_at.slice(0, 10) >= query.from)
          && (!query.to || d.created_at.slice(0, 10) <= query.to);
      })
      .sort((a, b) => b.created_at.localeCompare(a.created_at));

    const rows = query.file_type ? visible.filter((d) => d.current_version?.file_type_category === query.file_type) : visible;
    const page = paginate(rows, query, 25);

    return ok({
      documents: page.data.map(present),
      meta: { total: page.total, per_page: page.per_page, current_page: page.current_page, last_page: page.last_page },
      filters: {
        folder_types: unique(visible.map((d) => folderById(d.folder_id)?.folder_type)),
        file_types: unique(visible.map((d) => d.current_version?.file_type_category)),
      },
      summary: {
        documents: visible.length,
        folders: new Set(visible.map((d) => d.folder_id)).size,
        total_size: visible.reduce((sum, d) => sum + Number(d.current_version?.size_bytes ?? 0), 0),
      },
    });
  }],

  ["post", "tenant/documents", ({ body }) => {
    if (!folderById(body.folder_id)) return [403, { status: "error", code: 403, message: "You cannot upload to this folder", data: null, errors: null }];
    if (!String(body.name ?? "").trim()) return invalid({ name: ["The name field is required."] });
    const kind = categorize(body.file?.name);
    const doc = D(nextId(documents), Number(body.folder_id), body.name.trim(), body.description || null, kind, body.file?.size ?? 0, 1, 0);
    doc.current_version.mime_type = KINDS[kind] ?? "application/octet-stream";
    documents.push(doc);
    return created(doc, "Document uploaded");
  }],

  ["get", "tenant/documents/:id/download", ({ params }) => {
    const doc = documents.find((d) => String(d.id) === String(params.id));
    if (!doc) return notFound("Document not found");
    const v = doc.current_version;
    const filename = `${doc.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}.txt`;
    const text = `${doc.name}\n\nSample document (prototype mode): the real file is served from storage.`;
    return ok({ url: fileUrl(text, "text/plain"), filename, mime_type: v.mime_type, size_bytes: v.size_bytes, version_number: v.version_number, expires_in: 600 });
  }],

  ["delete", "tenant/documents/:id", ({ params }) => {
    const index = documents.findIndex((d) => String(d.id) === String(params.id));
    if (index < 0) return notFound("Document not found");
    const [doc] = documents.splice(index, 1);
    return ok({ id: doc.id, name: doc.name, description: doc.description }, "Document deleted");
  }],
];
